import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Wand2, Check, Sparkles, Lock } from "lucide-react";
import TopBar from "@/components/TopBar";
import FrozenBanner from "@/components/FrozenBanner";
import { useLanguage } from "@/i18n/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const IDEAS = [
  { key: "e1", color: "#D81B8C" },
  { key: "e2", color: "#3FA535" },
  { key: "e3", color: "#6B2C8F" },
  { key: "e4", color: "#D81B8C" },
  { key: "e5", color: "#3FA535" },
  { key: "e6", color: "#6B2C8F" },
];

export default function CustomGift() {
  const { t } = useLanguage();
  const { code } = useParams();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", wish: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [event, setEvent] = useState(null);

  useEffect(() => {
    api.get(`/public/events/${code}`)
      .then((r) => setEvent(r.data.event))
      .catch(() => setEvent(null));
  }, [code]);

  const frozen = !!event?.frozen;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.first_name.trim() || !form.last_name.trim() || !form.phone.trim() || !form.wish.trim()) {
      setError(t("custom.formError"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/public/custom-gift-requests", { ...form, event_code: code });
      setSuccess(true);
      setForm({ first_name: "", last_name: "", phone: "", wish: "" });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF9FB] pb-24">
      <TopBar />
      <div className="max-w-2xl mx-auto px-5">
        <button
          onClick={() => navigate(`/event/${code}`)}
          data-testid="custom-back"
          className="inline-flex items-center gap-2 text-sm text-[#5C4664] hover:text-[#2A0E3C]"
        >
          <ArrowLeft className="w-4 h-4" /> {t("product.backToEvent")}
        </button>

        <div className="mt-6">
          <div className="w-14 h-14 rounded-2xl bg-[#EFE3F5] text-[#6B2C8F] flex items-center justify-center mb-5">
            <Wand2 className="w-6 h-6" />
          </div>
          <h1 className="font-display text-3xl sm:text-5xl font-black text-[#2A0E3C] leading-tight">
            {t("custom.title")}
          </h1>
          <p className="mt-3 text-[#5C4664] text-lg">{t("custom.sub")}</p>
          <p className="mt-6 text-[#2A0E3C] leading-relaxed">{t("custom.intro")}</p>
        </div>

        {/* Ideas as plain paragraphs with colored titles */}
        <div className="mt-12">
          <h2 className="font-display text-2xl font-black text-[#2A0E3C] mb-6">
            {t("custom.examplesTitle")}
          </h2>
          <div className="space-y-6">
            {IDEAS.map(({ key, color }) => (
              <div key={key} data-testid={`custom-${key}`}>
                <h3 className="font-display text-lg font-bold leading-snug" style={{ color }}>
                  {t(`custom.${key}Title`)}
                </h3>
                <p className="mt-1 text-sm text-[#2A0E3C] leading-relaxed">
                  {t(`custom.${key}Desc`)}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA → opens form (locked when purchases are frozen) */}
        <div
          className="mt-14 rounded-3xl p-7 sm:p-9 text-white relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #D81B8C 0%, #6B2C8F 100%)" }}
        >
          <h3 className="font-display text-2xl sm:text-3xl font-black">
            {t("custom.ctaTitle")}
          </h3>
          <p className="mt-2 text-white/90">{t("custom.ctaSub")}</p>
          <Button
            onClick={() => { setSuccess(false); setError(""); setOpen(true); }}
            data-testid="custom-cta-btn"
            disabled={frozen}
            className={`milk-shimmer mt-5 rounded-full bg-white text-[#2A0E3C] hover:bg-white/90 h-12 px-7 gap-2 font-semibold ${
              frozen ? "opacity-60 cursor-not-allowed" : ""
            }`}
          >
            {frozen ? <Lock className="w-4 h-4 text-[#D81B8C]" /> : <Sparkles className="w-4 h-4 text-[#D81B8C]" />}
            {t("custom.ctaBtn")}
          </Button>
        </div>

        <FrozenBanner event={event} />
      </div>

      {/* Form / success modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="custom-form-modal">
          {success ? (
            <div className="text-center py-2">
              <div className="w-14 h-14 rounded-full bg-[#3FA535] text-white flex items-center justify-center mx-auto mb-3">
                <Check className="w-6 h-6" />
              </div>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-black text-[#2A0E3C]">
                  {t("custom.successTitle")}
                </DialogTitle>
                <DialogDescription className="text-[#5C4664]">
                  {t("custom.successSub")}
                </DialogDescription>
              </DialogHeader>
              <Button
                onClick={() => setOpen(false)}
                data-testid="custom-success-close"
                className="mt-5 rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] w-full h-12"
              >
                OK
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-black text-[#2A0E3C]">
                  {t("custom.formTitle")}
                </DialogTitle>
                <DialogDescription className="text-[#5C4664]">
                  {t("custom.ctaSub")}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Field label={t("custom.firstName")}>
                    <Input
                      data-testid="custom-firstname"
                      value={form.first_name}
                      onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                      className="h-11 rounded-xl border-[#F2E2EC]"
                    />
                  </Field>
                  <Field label={t("custom.lastName")}>
                    <Input
                      data-testid="custom-lastname"
                      value={form.last_name}
                      onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                      className="h-11 rounded-xl border-[#F2E2EC]"
                    />
                  </Field>
                </div>
                <Field label={t("custom.phone")}>
                  <Input
                    data-testid="custom-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+243…"
                    className="h-11 rounded-xl border-[#F2E2EC]"
                  />
                </Field>
                <Field label={t("custom.wish")}>
                  <Textarea
                    data-testid="custom-wish"
                    value={form.wish}
                    onChange={(e) => setForm({ ...form, wish: e.target.value })}
                    rows={3}
                    className="rounded-xl border-[#F2E2EC]"
                  />
                </Field>
                {error && (
                  <p data-testid="custom-form-error" className="text-sm text-[#D81B8C] font-medium">
                    {error}
                  </p>
                )}
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="custom-submit"
                  className="milk-shimmer milk-glow-pink w-full rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] h-12 font-semibold"
                >
                  {submitting ? t("common.loading") : t("custom.submit")}
                </Button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664]">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
